import React, { useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Send, RefreshCw, Copy } from '../ui/icons';
import { copyToClipboard } from '../../utils/clipboard';
import type { ColDef } from 'ag-grid-community';
import Card from '../ui/Card';
import Button from '../ui/Button';
import SearchableSelect from '../ui/SearchableSelect';
import DataTable from '../../utils/DataTable';
import {
  manualNotificationsApi,
  ManualNotificationRecord,
  ManualNotificationType,
  GameStreamStatus,
  NotificationSeverity,
  gamesApi,
  Game,
  teamApi,
  TeamRecord,
  THEME_MODE,
} from '../../services/api';
import Colors from '../../constants/Colors';

const ENV_PREFIX = THEME_MODE === 'prod' ? 'prod' : 'dev';

const TYPE_LABELS: Record<ManualNotificationType, string> = {
  game_stream: 'Game Stream',
  team: 'Team',
  generic: 'Generic',
};

const TYPE_INFO: Record<ManualNotificationType, { label: string; help: string }> = {
  generic: {
    label: 'Generic',
    help: `Broadcast to every user on the generic topic (homestream/${ENV_PREFIX}/generic_notifications). Use for announcements not tied to a specific game or team.`,
  },
  game_stream: {
    label: 'Game Stream Resume / Halt',
    help: `Sent to subscribers of one game (homestream/${ENV_PREFIX}/games/{game_id}/status) to say its stream has resumed or halted.`,
  },
  team: {
    label: 'Team',
    help: `Sent to subscribers of one team (homestream/${ENV_PREFIX}/teams/{team_id}).`,
  },
};

const SEVERITY_MAP: Record<string, { label: string; fg: string; bg: string; help: string }> = {
  '#FF3B30': {
    label: 'Red',
    fg: '#FF3B30',
    bg: 'rgba(255, 59, 48, 0.12)',
    help: 'No broadcast available at all (e.g. bridge/routing failure). Users cannot dismiss it — an admin must clear it once resolved.',
  },
  '#FF9500': {
    label: 'Amber',
    fg: '#FF9500',
    bg: 'rgba(255, 149, 0, 0.12)',
    help: 'Degraded but usable (e.g. sync is down but the manual adjuster works). Dismissible by the user.',
  },
  '#34C759': {
    label: 'Green',
    fg: '#34C759',
    bg: 'rgba(52, 199, 89, 0.12)',
    help: 'Services are back to normal. Dismissible by the user.',
  },
  red: {
    label: 'Red',
    fg: '#FF3B30',
    bg: 'rgba(255, 59, 48, 0.12)',
    help: 'No broadcast available at all (e.g. bridge/routing failure). Users cannot dismiss it — an admin must clear it once resolved.',
  },
  amber: {
    label: 'Amber',
    fg: '#FF9500',
    bg: 'rgba(255, 149, 0, 0.12)',
    help: 'Degraded but usable (e.g. sync is down but the manual adjuster works). Dismissible by the user.',
  },
  green: {
    label: 'Green',
    fg: '#34C759',
    bg: 'rgba(52, 199, 89, 0.12)',
    help: 'Services are back to normal. Dismissible by the user.',
  },
};

// Inline label + field rows, matching the Manual Operation form (label 128px, field up to 420px).
const LABEL_WIDTH = 128;
const LABEL_GAP = 14;

const rowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: `8px ${LABEL_GAP}px`,
};

const labelStyle: React.CSSProperties = {
  flex: `0 0 ${LABEL_WIDTH}px`,
  fontSize: '13px',
  fontWeight: 500,
  color: Colors.text,
  textAlign: 'right',
};

const fieldStyle: React.CSSProperties = {
  flex: '0 1 420px',
  minWidth: 0,
};

function FieldRow({
  label,
  htmlFor,
  align = 'center',
  children,
}: {
  label: string;
  htmlFor?: string;
  align?: 'center' | 'start';
  children: React.ReactNode;
}) {
  return (
    <div style={{ ...rowStyle, alignItems: align === 'start' ? 'flex-start' : 'center' }}>
      <label htmlFor={htmlFor} style={{ ...labelStyle, paddingTop: align === 'start' ? '10px' : 0 }}>
        {label}
      </label>
      <div style={fieldStyle}>{children}</div>
    </div>
  );
}

function CopyableCell({ value, label }: { value?: string | null; label: string }) {
  if (!value) return <span style={{ color: Colors.inactive }}>-</span>;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        maxWidth: '100%',
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={value}>
        {value}
      </span>
      <button
        type="button"
        onClick={() => copyToClipboard(value, `${label} copied`)}
        title={`Copy ${label.toLowerCase()}`}
        style={{
          flex: 'none',
          width: '20px',
          height: '20px',
          padding: 0,
          border: 0,
          background: 'transparent',
          display: 'grid',
          placeItems: 'center',
          cursor: 'pointer',
          color: Colors.inactive,
          borderRadius: '4px',
        }}
      >
        <Copy size={13} />
      </button>
    </span>
  );
}

export default function ManualOperationsPanel() {
  const [activeTab, setActiveTab] = useState<'new' | 'history'>('new');

  const [notifications, setNotifications] = useState<ManualNotificationRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const [games, setGames] = useState<Game[]>([]);
  const [teams, setTeams] = useState<TeamRecord[]>([]);

  const [notificationType, setNotificationType] = useState<ManualNotificationType>('generic');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [gameId, setGameId] = useState('');
  const [streamStatus, setStreamStatus] = useState<GameStreamStatus>('resumed');
  const [teamId, setTeamId] = useState('');
  const [severity, setSeverity] = useState<NotificationSeverity | ''>('');
  const [sending, setSending] = useState(false);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);

    const response = await manualNotificationsApi.list();
    if (response.success && response.data) {
      setNotifications(response.data.notifications || []);
    } else {
      setHistoryError(response.error || 'Failed to fetch notification history');
    }

    setHistoryLoading(false);
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const loadScheduledGames = useCallback(async () => {
    const response = await gamesApi.getScheduledTodayAndUpcomingGames();
    if (response.success && response.data) {
      const scheduledGames = (response.data.games || []).filter(
        (game) => String(game.status || '').toLowerCase() === 'scheduled'
      );
      setGames(scheduledGames);
    }
  }, []);

  const loadActiveTeams = useCallback(async () => {
    const response = await teamApi.listTeams({ status: 'active' });
    if (response.success && response.data) {
      setTeams(response.data.teams || []);
    }
  }, []);

  useEffect(() => {
    loadScheduledGames();
    loadActiveTeams();
  }, [loadScheduledGames, loadActiveTeams]);

  const gameOptions = useMemo(
    () =>
      games.map((game) => ({
        value: game.game_id,
        label: `${game.home_team} vs ${game.away_team} (${game.date})`,
        searchText: `${game.home_team} ${game.away_team} ${game.game_name || ''} ${game.venue || ''} ${game.date}`,
      })),
    [games]
  );

  const teamOptions = useMemo(
    () =>
      teams.map((team) => ({
        value: team.team_id,
        label: team.name,
        searchText: `${team.name} ${team.abbreviation || ''} ${team.school_name || ''}`,
      })),
    [teams]
  );

  const resetForm = () => {
    setTitle('');
    setMessage('');
    setGameId('');
    setStreamStatus('resumed');
    setTeamId('');
    setSeverity('');
  };

  const getComputedTopic = (): string => {
    if (notificationType === 'game_stream' && gameId) {
      return `homestream/${ENV_PREFIX}/games/${gameId}/status`;
    }
    if (notificationType === 'team' && teamId) {
      return `homestream/${ENV_PREFIX}/teams/${teamId}`;
    }
    return `homestream/${ENV_PREFIX}/generic_notifications`;
  };

  const handleSend = async () => {
    const missing: string[] = [];

    if (notificationType === 'game_stream' && !gameId) {
      missing.push('Game');
    }
    if (notificationType === 'team' && !teamId) {
      missing.push('Team');
    }
    if (!severity) {
      missing.push('Severity');
    }
    if (!title.trim()) {
      missing.push('Title');
    }
    if (!message.trim()) {
      missing.push('Message');
    }

    if (missing.length > 0) {
      let errorMsg = '';
      if (missing.length === 1) {
        errorMsg = `${missing[0]} is required`;
      } else if (missing.length === 2) {
        errorMsg = `${missing[0]} and ${missing[1]} are required`;
      } else {
        const copy = [...missing];
        const last = copy.pop();
        errorMsg = `${copy.join(', ')} and ${last} are required`;
      }
      toast.error(errorMsg);
      return;
    }

    const selectedGame = games.find((g) => g.game_id === gameId);
    const gameName = selectedGame
      ? (selectedGame.game_name || `${selectedGame.home_team} vs ${selectedGame.away_team}`)
      : undefined;

    const selectedTeam = teams.find((t) => t.team_id === teamId);
    const teamName = selectedTeam ? selectedTeam.name : undefined;

    setSending(true);
    const computedTopic = getComputedTopic();
    const response = await manualNotificationsApi.send({
      notification_type: notificationType,
      title: title.trim(),
      message: message.trim(),
      topic: computedTopic,
      severity: severity as NotificationSeverity,
      ...(notificationType === 'game_stream' ? { game_id: gameId, game_name: gameName, stream_status: streamStatus } : {}),
      ...(notificationType === 'team' ? { team_id: teamId, team_name: teamName } : {}),
    });
    setSending(false);

    if (!response.success || !response.data?.success) {
      toast.error(response.error || response.data?.error || 'Failed to save notification');
      return;
    }

    toast.success('Notification recorded in table');
    resetForm();
    fetchHistory();
  };

  const columnDefs = useMemo<ColDef<ManualNotificationRecord>[]>(
    () => [
      {
        field: 'notification_type',
        headerName: 'Type',
        width: 140,
        valueFormatter: (params) =>
          TYPE_LABELS[params.value as ManualNotificationType] || params.value,
      },
      {
        field: 'severity',
        headerName: 'Severity',
        width: 120,
        cellRenderer: (params: any) => {
          const raw = String(params.value || '').trim();
          const info = SEVERITY_MAP[raw] || SEVERITY_MAP[raw.toLowerCase()];
          if (!info) return '-';
          return (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '15px',
                width: 'fit-content',
                padding: '0px 10px',
                borderRadius: '2%',
                fontSize: '11px',
                fontWeight: 600,
                background: info.bg,
                color: info.fg,
              }}
            >
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: info.fg }} />
              {info.label}
            </span>
          );
        },
      },
      {
        field: 'title',
        headerName: 'Title',
        flex: 1.3,
        minWidth: 140,
        cellRenderer: (params: any) => <CopyableCell value={params.value} label="Title" />,
      },
      {
        field: 'message',
        headerName: 'Message',
        flex: 1.8,
        minWidth: 180,
        cellRenderer: (params: any) => <CopyableCell value={params.value} label="Message" />,
      },
      {
        field: 'game_name',
        headerName: 'Game Name',
        flex: 1.3,
        minWidth: 150,
        cellRenderer: (params: any) => <CopyableCell value={params.value} label="Game name" />,
      },
      {
        field: 'team_name',
        headerName: 'Team Name',
        flex: 1.1,
        minWidth: 130,
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'topic',
        headerName: 'Topic',
        flex: 1.6,
        minWidth: 180,
      },
      {
        field: 'created_at',
        headerName: 'Created At',
        flex: 1.2,
        minWidth: 160,
        valueFormatter: (params) => {
          if (!params.value) return '-';
          try {
            return new Date(params.value).toLocaleString();
          } catch {
            return params.value;
          }
        },
      },
    ],
    []
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <h2 style={{ fontSize: '20px', fontWeight: 700, color: Colors.text }}>
        In App Notifications
      </h2>

      <div className="teams-editor-tabs">
        <button
          type="button"
          className={`teams-editor-tab ${activeTab === 'new' ? 'active' : ''}`}
          onClick={() => setActiveTab('new')}
        >
          New Notification
        </button>
        <button
          type="button"
          className={`teams-editor-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Notification History
        </button>
      </div>

      {activeTab === 'new' && (
        <Card
          title="New Notification"
          description="Push a manual notification"
        >
          <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
            {/* Form block (label + gap + 420px field) centred in its column; fields stay compact. */}
            <div style={{ flex: '1 1 480px', minWidth: 0, display: 'flex', justifyContent: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', width: '100%', maxWidth: `${LABEL_WIDTH + LABEL_GAP + 420}px`, textAlign: 'left' }}>
              <FieldRow label="Type" htmlFor="notification-type">
                <select
                  id="notification-type"
                  className="form-input"
                  value={notificationType}
                  onChange={(e) => setNotificationType(e.target.value as ManualNotificationType)}
                >
                  <option value="generic">Generic</option>
                  <option value="game_stream">Game Stream Resume / Halt</option>
                  <option value="team">Team</option>
                </select>
              </FieldRow>

              {notificationType === 'game_stream' && (
                <FieldRow label="Game & Status">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    <SearchableSelect
                      id="notification-game"
                      value={gameId}
                      onChange={setGameId}
                      onOpen={loadScheduledGames}
                      options={gameOptions}
                      placeholder="Search scheduled games..."
                      emptyMessage="No scheduled games found"
                      style={{ flex: '1 1 180px', minWidth: 0 }}
                    />
                    <select
                      className="form-input"
                      value={streamStatus}
                      onChange={(e) => setStreamStatus(e.target.value as GameStreamStatus)}
                      style={{ flex: '1 1 180px', minWidth: 0 }}
                    >
                      <option value="resumed">Resumed</option>
                      <option value="halted">Halted</option>
                    </select>
                  </div>
                </FieldRow>
              )}

              {notificationType === 'team' && (
                <FieldRow label="Team" htmlFor="notification-team">
                  <SearchableSelect
                    id="notification-team"
                    value={teamId}
                    onChange={setTeamId}
                    onOpen={loadActiveTeams}
                    options={teamOptions}
                    placeholder="Search active teams..."
                    emptyMessage="No active teams found"
                  />
                </FieldRow>
              )}

              <FieldRow label="Severity" htmlFor="notification-severity">
                <select
                  id="notification-severity"
                  className="form-input"
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value as NotificationSeverity)}
                >
                  <option value="">Select severity...</option>
                  <option value="#FF3B30">Red</option>
                  <option value="#FF9500">Amber</option>
                  <option value="#34C759">Green</option>
                </select>
              </FieldRow>

              <FieldRow label="Title" htmlFor="notification-title">
                <input
                  id="notification-title"
                  className="form-input"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Notification title"
                />
              </FieldRow>

              <FieldRow label="Message" htmlFor="notification-message" align="start">
                <textarea
                  id="notification-message"
                  className="form-input"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Notification message"
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </FieldRow>

              <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '6px', marginLeft: `${LABEL_WIDTH + LABEL_GAP}px` }}>
                <Button onClick={handleSend} loading={sending} className="btn-primary">
                  <Send size={16} />
                  Record Notification
                </Button>
              </div>
            </div>
            </div>

            <div style={{ alignSelf: 'stretch', width: '1px', background: 'rgba(var(--color-border-rgb), 0.16)' }} />

            <div style={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '20px', textAlign: 'left' }}>
              <div>
                <h4 style={{ fontSize: '13px', fontWeight: 700, color: Colors.text, marginBottom: '10px' }}>Notification Types</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {(Object.keys(TYPE_INFO) as ManualNotificationType[]).map((key) => (
                    <div key={key}>
                      <div style={{ fontSize: '12.5px', fontWeight: 600, color: Colors.text }}>{TYPE_INFO[key].label}</div>
                      <div style={{ fontSize: '12px', color: Colors.textLight, marginTop: '2px' }}>{TYPE_INFO[key].help}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 style={{ fontSize: '13px', fontWeight: 700, color: Colors.text, marginBottom: '10px' }}>Severity Levels</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {(['#FF3B30', '#FF9500', '#34C759'] as string[]).map((key) => {
                    const info = SEVERITY_MAP[key];
                    return (
                      <div key={key} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <span
                          style={{
                            width: '8px', height: '8px', borderRadius: '50%',
                            background: info.fg, marginTop: '4px', flexShrink: 0,
                          }}
                        />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '12.5px', fontWeight: 600, color: info.fg }}>{info.label}</div>
                          <div style={{ fontSize: '12px', color: Colors.textLight, marginTop: '2px' }}>{info.help}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {activeTab === 'history' && (
        <Card
          title="Notification History"
          description="All manually triggered notifications, most recent first"
          headerAction={
            <Button
              onClick={fetchHistory}
              loading={historyLoading}
              className="btn-small btn-primary"
              style={{ padding: '0 12px' }}
            >
              <RefreshCw size={14} />
              Refresh
            </Button>
          }
        >
          {historyError && (
            <div className="alert alert-warning" style={{ marginBottom: '12px' }}>
              <span>{historyError}</span>
            </div>
          )}

          <div style={{ width: '100%' }}>
            <DataTable<ManualNotificationRecord>
              rowData={notifications}
              columnDefs={columnDefs}
              getRowId={(data) => data.notification_id}
              enableSearch={false}
              pagination={true}
              paginationPageSize={10}
              domLayout="autoHeight"
            />
          </div>
        </Card>
      )}
    </div>
  );
}
